import {
  Avatar,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import {
  CheckBoxOutlineBlankRounded,
  CheckBoxRounded,
  ClearRounded,
  DeleteRounded,
  UpdateRounded
} from '@material-ui/icons';
import { Alert, Autocomplete } from '@material-ui/lab';
import { unwrapResult } from '@reduxjs/toolkit';
import clsx from 'clsx';
import { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { RootState } from '../../app/store';
import HierarchySelect from '../../components/HierarchySelect';
import ProjectInfoCard from '../../components/ProjectInfoCard';
import displayRemovedUser from '../../functions/displayRemovedUser';
import sortMembers from '../../functions/sortMembers';
import { stringAvatar } from '../../functions/stringAvatar';
import { useAppDispatch } from '../../hooks/useAppRudux';
import useProjectUsers from '../../hooks/useProjectUsers';
import Breadcrumb from '../Breadcrumb';
import loadingSlice from '../Loading/loadingSlice';
import { AccessControl } from '../MasterData/AccessControl/accessControlSlice';
import { getProjectAsync } from '../Project/projectSlice';
import { User } from '../UserProfile/userProfileSlice';
import {
  createProjectAccessControlUsersAsync,
  deleteProjectAccessControlUsersAsync,
  getProjectAccessControlUsersAsync,
  updateProjectAccessControlUsersAsync
} from './projectAccessControlUserSlice';

interface Invitation {
  users: User[];
  accessControls: AccessControl[];
}
export interface MemberWithAccessControl {
  userId: number;
  user?: User;
  accessControls: AccessControl[];
}

export default function ProjectAccessControlUserEdit() {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const { data: accessControls } = useSelector(
    (state: RootState) => state.accessControls
  );
  const users = useProjectUsers(); // Environment-aware user selection
  const { selectedProject } = useSelector((state: RootState) => state.projects);
  const { data: projectAccessControlUsers } = useSelector(
    (state: RootState) => state.projectAccessControlUsers
  );

  const params = useParams<{ projectId: string }>();
  const projectId = Number.parseInt(params.projectId);

  const [invitation, setInvitation] = useState<Invitation>({
    users: [],
    accessControls: []
  });
  const [defaultMembers, setDefaultMembers] = useState<
    MemberWithAccessControl[]
  >([]);
  const [updateMembers, setUpdateMembers] = useState<MemberWithAccessControl[]>(
    []
  );
  const [deleteUser, setDeleteUser] = useState<MemberWithAccessControl | null>(
    null
  );
  const [openDelete, setOpenDelete] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const disabledUpdateButton = defaultMembers === updateMembers;
  const showAlertRequestAccess = users.some(
    (user) =>
      user.roles &&
      user.roles.length === 0 &&
      !projectAccessControlUsers.some(({ userId }) => userId === user.id)
  );
  const showAlertExistingAccess = updateMembers.some((member) =>
    users.some(
      (user) =>
        user.roles && user.roles.length === 0 && user.id === member.userId
    )
  );

  useEffect(() => {
    const memberLists = projectAccessControlUsers.map((member) => {
      const selectedUser = users.find((user) => user.id === member.userId);
      return {
        userId: member.userId,
        user: selectedUser,
        accessControls: member.accessControls
      };
    });
    setDefaultMembers(memberLists);
    setUpdateMembers(memberLists);
  }, [projectAccessControlUsers, users]);

  useEffect(() => {
    dispatch(getProjectAccessControlUsersAsync(projectId));
    dispatch(getProjectAsync(projectId));
  }, []);

  function onChangeAccessControl(userId: number, newValue: AccessControl[]) {
    const updateMemberIndex = updateMembers.findIndex(
      (updateMember) => updateMember.userId === userId
    );
    const newUpdateMember = {
      ...updateMembers[updateMemberIndex],
      accessControls: newValue
    };
    const newUpdateMembers = [
      ...updateMembers.slice(0, updateMemberIndex),
      newUpdateMember,
      ...updateMembers.slice(updateMemberIndex + 1)
    ];
    setUpdateMembers(newUpdateMembers);
  }

  function handleDelete(deleteUser: MemberWithAccessControl) {
    setDeleteUser(deleteUser);
    setOpenDelete(true);
  }

  function confirmDelete() {
    if (deleteUser) {
      dispatch(
        deleteProjectAccessControlUsersAsync({
          projectId,
          userId: deleteUser.userId
        })
      );
      setOpenDelete(false);
    }
  }

  async function onCreateMember(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    try {
      setLoading(true);
      const result = await dispatch(
        createProjectAccessControlUsersAsync({
          projectId,
          projectAccessControlUsers: invitation.users.map((user) => {
            return {
              userId: user.id,
              accessControlIds: invitation.accessControls.map(
                (accessControl) => accessControl.id
              )
            };
          })
        })
      );
      await unwrapResult(result);
      dispatch(
        loadingSlice.actions.success({
          message: t('projectAccessControlUser.messages.usersAdded')
        })
      );
      setInvitation({ users: [], accessControls: [] });
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    } catch (error) {
      setLoading(false);
    }
  }

  async function onUpdateAccessControl(
    event: React.FormEvent<HTMLFormElement>
  ) {
    event.preventDefault();
    try {
      setLoading(true);
      const result = await dispatch(
        updateProjectAccessControlUsersAsync({
          projectId,
          projectAccessControlUsers: updateMembers.map((updateMember) => {
            return {
              userId: updateMember.userId,
              accessControlIds: updateMember.accessControls.map(
                (accessControl) => accessControl.id
              )
            };
          })
        })
      );
      await unwrapResult(result);
      dispatch(
        loadingSlice.actions.success({
          message: t('projectAccessControlUser.messages.accessControlsUpdated')
        })
      );
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    } catch (error) {
      setLoading(false);
    }
  }

  return (
    <div className={classes.root}>
      <Dialog
        fullWidth
        maxWidth="xs"
        open={openDelete}
        onClose={() => setOpenDelete(false)}
      >
        <DialogTitle id="title">{t('projectAccessControlUser.confirmDelete.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText id="description">
            {t('projectAccessControlUser.confirmDelete.message', {
              userName: deleteUser && deleteUser.user
                ? `${deleteUser.user.firstName} ${deleteUser.user.lastName}`
                : t('projectAccessControlUser.removedUser')
            })}
          </DialogContentText>
        </DialogContent>
        <div className={classes.flexAction}>
          <Button
            className={classes.redColor}
            fullWidth
            color="primary"
            variant="contained"
            onClick={confirmDelete}
          >
            {t('projectAccessControlUser.actions.remove')}
          </Button>
          <Button fullWidth onClick={() => setOpenDelete(false)}>
            {t('projectAccessControlUser.actions.cancel')}
          </Button>
        </div>
      </Dialog>
      <Breadcrumb
        name="editProjectMember"
        data={{
          projectName: selectedProject?.name,
          projectId: selectedProject?.id
        }}
      />
      <Typography className={classes.title} component="p" variant="h4">
        {t('projectAccessControlUser.title')}
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={3} className={classes.showInformation}>
          {selectedProject && <ProjectInfoCard project={selectedProject} />}
        </Grid>
        <Grid item xs={12} md={9}>
          <Paper className={classes.card}>
            {showAlertRequestAccess && (
              <Alert
                className={classes.alert}
                severity="info"
                icon={false}
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => {
                      window.open(
                        `${process.env.REACT_APP_REQUEST_FOR_BACKSPACE_WEB_URL}?systemName=doc`,
                        '_blank'
                      );
                    }}
                  >
                    {t('projectAccessControlUser.actions.requestAccess')}
                  </Button>
                }
              >
                {t('projectAccessControlUser.alerts.spaceDocumentAccess')}
              </Alert>
            )}
            <form onSubmit={onCreateMember}>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={invitation.users.length === 0 ? 12 : 8}>
                  <Autocomplete
                    id="add-member"
                    size="small"
                    multiple
                    options={users.filter(
                      (user) =>
                        !projectAccessControlUsers.some(
                          (projectAccessControlUser) =>
                            projectAccessControlUser.userId === user.id
                        )
                    )}
                    getOptionLabel={(option) =>
                      `${option?.firstName} ${option?.lastName}`
                    }
                    getOptionDisabled={(option) => option.roles?.length === 0}
                    renderTags={(tagValue, getTagProps) =>
                      tagValue.map((option, index) => (
                        <Chip
                          label={`${option?.firstName} ${option?.lastName}`}
                          {...getTagProps({ index })}
                          variant="outlined"
                          size="small"
                          deleteIcon={<ClearRounded />}
                        />
                      ))
                    }
                    value={invitation.users}
                    getOptionSelected={(option, value) =>
                      option.id === value?.id
                    }
                    onChange={(event, newValue) => {
                      setInvitation({
                        ...invitation,
                        users: newValue,
                        accessControls:
                          newValue.length === 0 ? [] : invitation.accessControls
                      });
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        placeholder={
                          invitation.users.length === 0 ? (t('projectAccessControlUser.placeholders.selectMember') as string) : ''
                        }
                      />
                    )}
                  />
                </Grid>
                {invitation.users.length > 0 && (
                  <Grid item xs={12} sm={4}>
                    <HierarchySelect<AccessControl>
                      placeholder={t('projectAccessControlUser.placeholders.accessLevel') as string}
                      options={accessControls}
                      value={invitation.accessControls}
                      onChange={(accessControls) =>
                        setInvitation({
                          ...invitation,
                          accessControls
                        })
                      }
                      required={invitation.users.length > 0}
                    />
                  </Grid>
                )}
              </Grid>
              <Collapse in={invitation.users.length > 0}>
                <div className={classes.collapseButton}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    fullWidth
                    disabled={invitation.accessControls.length === 0}
                  >
                    {t('projectAccessControlUser.actions.add')}
                  </Button>
                  <Button
                    className={classes.marginLeft}
                    fullWidth
                    onClick={() =>
                      setInvitation({ users: [], accessControls: [] })
                    }
                  >
                    {t('projectAccessControlUser.actions.cancel')}
                  </Button>
                </div>
              </Collapse>
            </form>
            {updateMembers.length > 0 ? (
              <form onSubmit={onUpdateAccessControl}>
                <Grid
                  className={classes.paddingTopBottom}
                  container
                  spacing={1}
                >
                  {sortMembers<MemberWithAccessControl>(updateMembers).map(
                    (updateMember) => {
                      const removedUser = !updateMember.user;
                      const user = users.find(
                        ({ id }) => id === updateMember.userId
                      );
                      const notHasPermission = user?.roles?.length === 0;
                      return (
                        <Fragment key={updateMember.userId}>
                          <Grid
                            className={classes.flexAlignCenter}
                            item
                            xs={12}
                            sm={4}
                          >
                            <Avatar
                              {...stringAvatar(
                                displayRemovedUser(
                                  updateMember.userId,
                                  updateMember.user
                                )
                              )}
                            />
                            <div className={classes.styleUserName}>
                              <Typography
                                className={classes.lineHeight120}
                                color={
                                  removedUser ? 'textSecondary' : 'textPrimary'
                                }
                              >
                                {displayRemovedUser(
                                  updateMember.userId,
                                  updateMember.user
                                )}
                              </Typography>
                              {notHasPermission && (
                                <Typography
                                  className={classes.lineHeight120}
                                  variant="caption"
                                  color="error"
                                >
                                  {t('projectAccessControlUser.notAuthorized')}
                                </Typography>
                              )}
                            </div>
                          </Grid>
                          <Grid
                            className={classes.flexAlignCenter}
                            item
                            xs={12}
                            sm={8}
                          >
                            <HierarchySelect<AccessControl>
                              placeholder={t('projectAccessControlUser.placeholders.accessLevel') as string}
                              options={accessControls}
                              value={updateMember.accessControls}
                              onChange={(accessControls) =>
                                onChangeAccessControl(
                                  updateMember.userId,
                                  accessControls
                                )
                              }
                              required
                              disabled={loading || invitation.users.length > 0}
                            />
                            <Tooltip title={t('projectAccessControlUser.tooltips.remove') as string} arrow>
                              <IconButton
                                className={classes.marginLeft}
                                size="small"
                                onClick={() => handleDelete(updateMember)}
                                disabled={
                                  loading || invitation.users.length > 0
                                }
                              >
                                <DeleteRounded />
                              </IconButton>
                            </Tooltip>
                          </Grid>
                        </Fragment>
                      );
                    }
                  )}
                </Grid>
                <Collapse
                  in={invitation.users.length === 0 && !disabledUpdateButton}
                >
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    size="large"
                    fullWidth
                    disabled={loading}
                    startIcon={<UpdateRounded />}
                  >
                    {t('projectAccessControlUser.actions.updateAccessControl')}
                  </Button>
                </Collapse>
              </form>
            ) : (
              <div className={classes.fileNotFound}>{t('projectAccessControlUser.memberNotFound')}</div>
            )}
            {showAlertExistingAccess && (
              <Alert
                className={classes.marginBottom}
                severity="error"
                icon={false}
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => {
                      window.open(
                        `${process.env.REACT_APP_REQUEST_FOR_BACKSPACE_WEB_URL}?systemName=doc`,
                        '_blank'
                      );
                    }}
                  >
                    {t('projectAccessControlUser.actions.requestAccess')}
                  </Button>
                }
              >
                {t('projectAccessControlUser.alerts.contactBackspaceSupport')}
              </Alert>
            )}
          </Paper>
        </Grid>
      </Grid>
    </div>
  );
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      margin: theme.spacing(1.5, 0, 12, 0)
    },
    title: {
      margin: theme.spacing(1.5, 0, 2, 0),
      [theme.breakpoints.down('xs')]: {
        textAlign: 'center',
        margin: theme.spacing(3.5, 0, 4, 0)
      }
    },
    card: {
      padding: theme.spacing(2, 2, 1, 2),
      borderRadius: theme.spacing(2)
    },
    fileNotFound: {
      padding: theme.spacing(6, 2),
      textAlign: 'center'
    },
    alert: {
      marginBottom: theme.spacing(1)
    },
    marginLeft: {
      marginLeft: theme.spacing(1)
    },
    marginRight: {
      marginRight: theme.spacing(1)
    },
    marginBottom: {
      marginBottom: theme.spacing(1)
    },
    paddingTopBottom: {
      padding: theme.spacing(2, 0, 1, 0)
    },
    flexAlignCenter: {
      display: 'flex',
      alignItems: 'center'
    },
    flexAction: {
      display: 'flex',
      '& > *': {
        margin: theme.spacing(1)
      },
      [theme.breakpoints.down('xs')]: {
        flexDirection: 'column-reverse',
        width: `calc(100% - ${theme.spacing(2)}px)`,
        '& > *': {
          marginTop: 0
        }
      }
    },
    redColor: {
      backgroundColor: theme.palette.error.main,
      '&:hover, &.Mui-focusVisible': {
        backgroundColor: theme.palette.error.dark
      }
    },
    fullWidth: {
      width: '100%'
    },
    checkIcon: {
      margin: theme.spacing(-2)
    },
    collapseButton: {
      display: 'flex',
      padding: theme.spacing(1, 0, 2, 0)
    },
    styleUserName: {
      marginLeft: theme.spacing(1),
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(-1)
    },
    lineHeight120: {
      lineHeight: '120%'
    },
    showInformation: {
      [theme.breakpoints.down('sm')]: {
        display: 'none'
      }
    }
  })
);
